<template>
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
    @click.self="close"
  >
    <div class="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
      <!-- Success state -->
      <div v-if="done" class="text-center py-6">
        <div class="text-4xl mb-3">🙏</div>
        <h3 class="text-lg font-bold text-gray-900">Thanks — we'll take a look</h3>
        <p class="text-sm text-gray-500 mt-1">Your report was sent to our team.</p>
        <button
          @click="close"
          class="mt-5 bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-emerald-700"
        >
          Close
        </button>
      </div>

      <!-- Form -->
      <div v-else>
        <div class="flex items-start justify-between">
          <h3 class="text-lg font-bold text-gray-900">Report a problem</h3>
          <button @click="close" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <p class="text-sm text-gray-500 mt-1">Spotted something wrong with this exercise? Let us know.</p>

        <fieldset class="mt-4 space-y-2">
          <label
            v-for="cat in categories"
            :key="cat.value"
            class="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
            :class="category === cat.value ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'"
          >
            <input type="radio" name="report-category" :value="cat.value" v-model="category" class="accent-emerald-600" />
            <span class="text-sm font-medium text-gray-800">{{ cat.label }}</span>
          </label>
        </fieldset>

        <textarea
          v-model="comment"
          rows="3"
          maxlength="1000"
          placeholder="Anything else we should know? (optional)"
          class="mt-3 w-full rounded-xl border border-gray-200 p-3 text-sm focus:border-emerald-400 focus:ring-0"
        ></textarea>

        <p v-if="error" class="text-sm text-red-500 mt-2">{{ error }}</p>

        <div class="mt-4 flex gap-3 justify-end">
          <button
            @click="close"
            class="px-5 py-2.5 rounded-xl font-medium text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            @click="submit"
            :disabled="!category || submitting"
            class="px-5 py-2.5 rounded-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ submitting ? 'Sending…' : 'Send report' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { apiPost } from '../../api/client'

const props = defineProps({
  exercise: { type: Object, default: () => ({}) },
  unitId: { type: Number, default: null },
  level: { type: Number, default: null },
  reviewMode: { type: Boolean, default: false },
  demoMode: { type: Boolean, default: false },
  submittedAnswer: { type: String, default: '' },
})

const emit = defineEmits(['close', 'submitted'])

// Values must match ExerciseReport::CATEGORIES on the backend.
const categories = [
  { value: 'wrong_translation', label: 'The translation is wrong' },
  { value: 'wrong_answer_key', label: 'The answer key is wrong' },
  { value: 'marked_correct_wrong', label: 'It marked my correct answer wrong' },
  { value: 'duplicate', label: 'Duplicate exercise' },
  { value: 'typo', label: 'Typo or spelling mistake' },
  { value: 'other', label: 'Something else' },
]

const category = ref('')
const comment = ref('')
const submitting = ref(false)
const done = ref(false)
const error = ref('')

async function submit() {
  if (!category.value || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    await apiPost('/api/v1/exercise_reports', {
      category: category.value,
      comment: comment.value,
      submitted_answer: props.submittedAnswer,
      unit_id: props.unitId,
      level: props.level,
      exercise: props.exercise,
      context: {
        review_mode: props.reviewMode,
        demo_mode: props.demoMode,
        url: window.location.href,
      },
    })
    done.value = true
    emit('submitted')
  } catch (e) {
    error.value = "Sorry, that didn't send. Please try again."
  } finally {
    submitting.value = false
  }
}

function close() {
  emit('close')
}
</script>
